import * as React from 'react';
import BPromise from 'bluebird';
import moment from 'moment';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { DATE_FORMAT } from 'Src/consts/date';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import {
  getAllSuperAdminSchoolSubscriptions,
  getSuperAdminSchoolSubscriptions,
  getSuperAdminSchoolSubscriptionsCount,
  SuperAdminSchoolSubscription
} from 'Src/helpers/service/superadmin/subscriptions';
import './SuperAdminSchoolSubscriptions.css';

const SUBSCRIPTION_TYPE_OPTIONS = [{ text: 'SMS', value: 'SMS' }];

const SUBSCRIPTION_PROVIDER_OPTIONS = [
  { text: 'Twilio', value: 'TWILIO' },
  { text: 'Twilio (legacy typo)', value: 'TWILLIO' }
];

const SUBSCRIPTION_STATUS_OPTIONS = [
  { text: 'Active', value: 'ACTIVE' },
  { text: 'Suspended', value: 'SUSPENDED' },
  { text: 'Expired', value: 'EXPIRED' },
  { text: 'Terminated', value: 'TERMINATED' }
];

function formatDate(value?: string): string {
  if (!value) {
    return '';
  }

  const date = moment(value);
  return date.isValid() ? date.format(DATE_FORMAT) : '';
}

function formatStatus(subscription: SuperAdminSchoolSubscription): string {
  const status = subscription.status || '';
  const option = SUBSCRIPTION_STATUS_OPTIONS.find(item => item.value === status);

  return option ? String(option.text) : status;
}

function formatProvider(subscription: SuperAdminSchoolSubscription): string {
  const provider = subscription.provider || '';
  const option = SUBSCRIPTION_PROVIDER_OPTIONS.find(item => item.value === provider);

  return option ? String(option.text) : provider;
}

function formatPurchaseDate(subscription: SuperAdminSchoolSubscription): string {
  return formatDate(subscription.purchaseDate);
}

function formatExpireDate(subscription: SuperAdminSchoolSubscription): string {
  return formatDate(subscription.expireDate);
}

function formatPackageVolume(subscription: SuperAdminSchoolSubscription): string {
  return typeof subscription.packageVolume === 'number' ? String(subscription.packageVolume) : '';
}

function formatPhoneNumber(subscription: SuperAdminSchoolSubscription): string {
  if (typeof subscription.phoneNumber === 'number') {
    return String(subscription.phoneNumber);
  }

  return '';
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Type',
    field: 'type',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: ['type']
  },
  {
    text: 'Provider',
    field: 'provider',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: formatProvider
  },
  {
    text: 'Status',
    field: 'status',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: formatStatus
  },
  {
    text: 'Project name',
    field: 'projectName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['projectName']
  },
  {
    text: 'From',
    field: 'from',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['from']
  },
  {
    text: 'Phone number',
    field: 'phoneNumber',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: formatPhoneNumber
  },
  {
    text: 'Package volume',
    field: 'packageVolume',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: formatPackageVolume
  },
  {
    text: 'Purchase date',
    field: 'purchaseDate',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: formatPurchaseDate
  },
  {
    text: 'Expire date',
    field: 'expireDate',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: formatExpireDate
  },
  {
    text: 'Note',
    field: 'note',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['note']
  }
];

interface Props {
  schoolId: string;
  history: History;
  location: Location;
}

interface State {
  items: SuperAdminSchoolSubscription[];
  filterOptions: any;
  currentPage: number;
  selectedItems: SuperAdminSchoolSubscription[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
}

function removeEmptyAnd(where: any): any {
  const whereCopy = { ...where };

  if (Array.isArray(whereCopy.$and) && whereCopy.$and.length === 0) {
    delete whereCopy.$and;
  }

  return whereCopy;
}

function getFiltersWithoutSubscriptionDateIntervals(filters: any): any {
  const filtersCopy = { ...filters };

  delete filtersCopy.purchaseDate;
  delete filtersCopy.expireDate;

  return filtersCopy;
}

function addDateIntervalWhere(where: any, filters: any, field: string): void {
  const filter = filters[field];

  if (typeof filter !== 'object' || filter === null) {
    return;
  }

  if (typeof filter.to !== 'undefined' && filter.to !== '') {
    const toFormatted = moment(filter.to, DATE_FORMAT).toDate();
    toFormatted.setHours(23);
    toFormatted.setMinutes(59);

    where[field] = { ...where[field] };
    where[field].$lte = toFormatted.toISOString();
  }

  if (typeof filter.from !== 'undefined' && filter.from !== '') {
    const fromFormatted = moment(filter.from, DATE_FORMAT).toDate();

    where[field] = { ...where[field] };
    where[field].$gte = fromFormatted.toISOString();
  }
}

export class SuperAdminSchoolSubscriptions extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      filterOptions: this.getFilterOptions(),
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '' });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search || nextProps.schoolId !== this.props.schoolId) {
      this.setState({ isLoading: true, errorMessage: '' });
      this.setItems(nextProps);
    }
  }

  getFilterOptions() {
    return {
      type: SUBSCRIPTION_TYPE_OPTIONS,
      provider: SUBSCRIPTION_PROVIDER_OPTIONS,
      status: SUBSCRIPTION_STATUS_OPTIONS
    };
  }

  getWhere(filters: any): any {
    const filtersWithoutDateIntervals = getFiltersWithoutSubscriptionDateIntervals(filters);
    const where = getServerFieldSectionWhere(filtersWithoutDateIntervals, { isCorrectTime: true });

    addDateIntervalWhere(where, filters, 'purchaseDate');
    addDateIntervalWhere(where, filters, 'expireDate');

    return removeEmptyAnd(where);
  }

  setItems(props: Props = this.props) {
    const { history, schoolId } = props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = this.getWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'purchaseDate DESC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([
      getSuperAdminSchoolSubscriptionsCount(schoolId, where),
      getSuperAdminSchoolSubscriptions(schoolId, serverQueryFilter)
    ])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin school subscriptions', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load subscriptions. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = this.getWhere(filters);

    this.setState({ isLoading: true, errorMessage: '' });

    getAllSuperAdminSchoolSubscriptions(this.props.schoolId, where)
      .then(items => {
        const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin school subscriptions', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all subscriptions. Please try again.'
        });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      nextFilters = {
        ...nextFilters,
        [filterField]: filterValue
      };
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    this.setState({
      isShowFilter: !this.state.isShowFilter
    });
  };

  render() {
    const {
      currentPage,
      errorMessage,
      filters,
      filterOptions,
      isDataFiltered,
      isLoading,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      total
    } = this.state;

    const actionItems: ActionItem[] = [
      {
        itemText: 'View subscription',
        isActive: false,
        onItemClick: () => undefined
      }
    ];

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="SuperAdminSchoolSubscriptions">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Notification subscriptions</h4>
            <div className="text-muted">Read-only SMS/Twilio notification subscription list.</div>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => this.setItems()}>
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={COLUMNS}
          actionItems={actionItems}
          options={filterOptions}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          onTableFilterClick={this.onTableFilterClick}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
          isCSV={true}
        />
      </div>
    );
  }
}



